import React, { useState, useEffect } from 'react';
import { Table, Tag, Button, DatePicker, Card } from 'antd';
import { SyncOutlined, CalendarOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { TransactionResponse } from '../../model/Transaction.model';
import TransactionService from '../../service/transaction-service/Transaction.service';
import { formatCurrency } from '../withdrawal-check/utils';
import { formatCustomerFee } from '../withdrawal-check/utils';

interface DeletedWithdrawalTransactionListProps {
  creditCardId: number;
  selectedYear?: number;
  onYearChange?: (year: number) => void;
}

const DeletedWithdrawalTransactionList: React.FC<
  DeletedWithdrawalTransactionListProps
> = ({ creditCardId, selectedYear, onYearChange }) => {
  const [deletedTransactions, setDeletedTransactions] = useState<
    TransactionResponse[]
  >([]);
  const [loading, setLoading] = useState(false);

  // Use provided selectedYear or default to current year
  const currentYear = selectedYear || new Date().getFullYear();

  useEffect(() => {
    if (creditCardId) {
      loadDeletedTransactions();
    }
  }, [creditCardId, currentYear]);

  const loadDeletedTransactions = async () => {
    try {
      setLoading(true);
      const response = selectedYear
        ? await TransactionService.getDeletedWithdrawalTransactionsByCreditCardAndYear(
            creditCardId,
            currentYear
          )
        : await TransactionService.getDeletedWithdrawalTransactionsByCreditCard(
            creditCardId
          );

      if (response.success) {
        setDeletedTransactions(response.data || []);
      }
    } catch (error: any) {
      console.error('Error loading deleted withdrawal transactions:', error);
      // Không hiển thị message error cho việc load dữ liệu
    } finally {
      setLoading(false);
    }
  };

  // Handle year change
  const handleYearChange = (year: dayjs.Dayjs | null) => {
    if (year && onYearChange) {
      onYearChange(year.year());
    }
  };

  const columns = [
    {
      title: 'Ngày GD',
      dataIndex: 'transactionDate',
      key: 'transactionDate',
      width: 100,
      render: (date: string) => {
        const dateObj = new Date(date);
        return dateObj.toLocaleDateString('vi-VN');
      },
    },
    {
      title: 'Số tiền',
      dataIndex: 'amount',
      key: 'amount',
      width: 120,
      render: (amount: number) => (
        <span className="font-medium text-blue-600">
          {formatCurrency(amount)}
        </span>
      ),
    },
    {
      title: 'Phí KH',
      dataIndex: 'feeForCustomer',
      key: 'feeForCustomer',
      width: 100,
      render: (fee: number, record: TransactionResponse) => {
        if (!fee) return '-';
        return formatCustomerFee(fee, record.customerType || '%');
      },
    },
    {
      title: 'Phí POS (%)',
      dataIndex: 'feeApplied',
      key: 'feeApplied',
      width: 80,
      render: (fee: number) => (fee ? `${fee}%` : '0%'),
    },
    {
      title: 'Phí CTV (%)',
      dataIndex: 'commissionRateForPartner',
      key: 'commissionRateForPartner',
      width: 80,
      render: (commission: number) => (
        <span className="text-purple-500 text-xs">
          {commission ? `${commission}%` : 'N/A'}
        </span>
      ),
    },
    {
      title: 'Thực nhận',
      dataIndex: 'balance',
      key: 'balance',
      width: 120,
      render: (balance: number) => (
        <span className="font-medium text-green-600">
          {formatCurrency(balance)}
        </span>
      ),
    },
    {
      title: 'Thiết bị POS',
      key: 'posDevice',
      width: 150,
      render: (record: TransactionResponse) => {
        if (!record.posDevice) return '-';
        return (
          <div className="text-xs">
            <div className="font-medium">{record.posDevice.deviceCode}</div>
            <div className="text-gray-500">{record.posDevice.location}</div>
          </div>
        );
      },
    },
    {
      title: 'Nhân viên',
      key: 'employee',
      width: 120,
      render: (record: TransactionResponse) => {
        if (!record.employee) return '-';
        return (
          <div className="text-xs">
            <div className="font-medium">{record.employee.name}</div>
            <div className="text-gray-500">{record.employee.contactInfo}</div>
          </div>
        );
      },
    },
    {
      title: 'Trạng thái',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status: string) => (
        <Tag color={status === 'COMPLETED' ? 'green' : 'orange'}>
          {status === 'COMPLETED' ? 'Hoàn thành' : 'Đang chờ'}
        </Tag>
      ),
    },
    {
      title: 'Trạng thái',
      key: 'deletedStatus',
      width: 80,
      render: () => <Tag color="red">Đã xóa</Tag>,
    },
  ];

  return (
    <div>
      {/* Header with year picker */}
      <Card size="small" className="mb-4">
        <div className="flex justify-between items-center">
          <div className="flex items-center space-x-4">
            <CalendarOutlined className="text-blue-500" />
            <span className="text-sm font-medium">Chọn năm:</span>
            <DatePicker
              picker="year"
              value={selectedYear ? dayjs().year(selectedYear) : undefined}
              onChange={handleYearChange}
              format="YYYY"
              allowClear={false}
              className="w-32"
            />
          </div>
          <div className="flex items-center space-x-2">
            <Button
              type="primary"
              icon={<SyncOutlined />}
              onClick={loadDeletedTransactions}
              loading={loading}
              size="small"
            >
              Tải lại
            </Button>
          </div>
        </div>
      </Card>

      <div className="mb-4 flex justify-between items-center">
        <div>
          <h4 className="text-lg font-medium mb-2">
            Giao dịch rút tiền đã xóa
          </h4>
          <p className="text-gray-600 text-sm">
            Danh sách các giao dịch rút tiền đã bị xóa của thẻ này theo năm{' '}
            {currentYear}.
          </p>
        </div>
      </div>

      <Table
        columns={columns}
        dataSource={deletedTransactions}
        rowKey="id"
        loading={loading}
        pagination={{
          pageSize: 20,
          showSizeChanger: false,
          showQuickJumper: true,
          showTotal: (total, range) =>
            `${range[0]}-${range[1]} của ${total} giao dịch đã xóa`,
        }}
        scroll={{ x: 1080 }}
        size="small"
        bordered
        locale={{
          emptyText: 'Không có giao dịch nào bị xóa',
        }}
      />
    </div>
  );
};

export default DeletedWithdrawalTransactionList;
